// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ArtworkRegionCitationLink.test.tsx
import { FileMagnifyingGlass } from "@phosphor-icons/react";
import { Dialog, Popover, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  PACK_CHIP_CLASS,
  PackCitationDialogContent,
} from "./ArtworkRegionCitationLink";

import { type CitedPack } from "../../utils/packCitations";

/** How much of a printed wording an entry shows; the dialog shows it whole. */
const ENTRY_WORDING_CHARS = 90;

function truncate(text: string): string {
  return text.length > ENTRY_WORDING_CHARS
    ? `${text.slice(0, ENTRY_WORDING_CHARS - 1).trimEnd()}…`
    : text;
}

/**
 * One cited pack: a chip naming it with its claim and logo counts, which opens
 * a list of the spots the answer cites on it. Each entry lists the lines
 * printed in that box with their status and confidence, and opens the artwork
 * there.
 */
export function PackCitationChip({
  pack,
  testIdPrefix,
}: {
  pack: CitedPack;
  /** Unique per message and artwork: two packs, or two answers, are never one id. */
  testIdPrefix: string;
}) {
  const { t } = useTranslation("ui");
  const [listOpen, setListOpen] = useState(false);
  // why the spot's key and not its index: a poll replaces `pack` under the same chip, and an index would point the
  // open dialog at whatever spot now sits there
  const [openSpotKey, setOpenSpotKey] = useState<string | null>(null);
  const parts: string[] = [];
  if (pack.claims > 0) {
    parts.push(
      `${t("packCitation.claimCount", { count: pack.claims })} ${t(
        "packCitation.statusBreakdown",
        { exact: pack.exact, possible: pack.possible },
      )}`,
    );
  }
  if (pack.logos > 0) {
    parts.push(t("packCitation.logoCount", { count: pack.logos }));
  }
  const summary = parts.join(", ");
  const pages = pack.pages.join(",");
  const spot =
    openSpotKey != null
      ? pack.spots.find((one) => one.key === openSpotKey)
      : undefined;
  // why cleared here and not only in onOpenChange: Radix calls that on a user's close alone, so a poll that drops the
  // open spot would keep its key, and a later poll that restores the spot would reopen the dialog unasked
  useEffect(() => {
    if (openSpotKey != null && spot == null) setOpenSpotKey(null);
  }, [openSpotKey, spot]);

  return (
    <>
      <Popover.Root open={listOpen} onOpenChange={setListOpen}>
        <Popover.Trigger>
          <button
            type="button"
            data-testid={`${testIdPrefix}-chip`}
            aria-label={t("packCitation.packChipAriaLabel", {
              label: pack.label,
              summary,
            })}
            className={PACK_CHIP_CLASS}
          >
            <FileMagnifyingGlass
              size={13}
              className="text-puntt-accent-11"
              aria-hidden="true"
            />
            <span>
              {t("packCitation.packChip", {
                label: pack.label,
                pages,
                summary,
              })}
            </span>
          </button>
        </Popover.Trigger>
        <Popover.Content
          size="1"
          maxWidth="420px"
          className="max-h-[min(70vh,420px)] overflow-y-auto p-1"
          data-testid={`${testIdPrefix}-popover`}
          aria-label={t("packCitation.listAriaLabel", { label: pack.label })}
        >
          <ul
            className="m-0 flex list-none flex-col gap-1 p-0"
            data-testid={`${testIdPrefix}-list`}
            aria-label={t("packCitation.listAriaLabel", { label: pack.label })}
          >
            {pack.spots.map((one, index) => {
              const wording = one.citations
                .map((citation) => citation.quote || citation.label)
                .join(" / ");
              return (
                <li key={one.key}>
                  <button
                    type="button"
                    data-testid={`${testIdPrefix}-entry-${index}`}
                    aria-label={t("packCitation.entryAriaLabel", {
                      wording: truncate(wording),
                    })}
                    onClick={() => {
                      setListOpen(false);
                      setOpenSpotKey(one.key);
                    }}
                    className="flex w-full cursor-pointer flex-col gap-1 rounded-md border-0 bg-transparent px-2 py-1.5 text-left hover:bg-puntt-neutral-gray-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
                  >
                    {one.citations.map((citation, line) => (
                      <span
                        key={`${citation.label}-${line}`}
                        className="flex items-baseline justify-between gap-3"
                      >
                        <Text size="1" className="text-puntt-neutral-gray-12">
                          {truncate(citation.quote || citation.label)}
                        </Text>
                        <Text
                          size="1"
                          className="shrink-0 text-puntt-neutral-gray-11"
                        >
                          {t("packCitation.confidenceShort", {
                            status: t(
                              citation.status === "exact"
                                ? "packCitation.statusExact"
                                : "packCitation.statusPossible",
                            ),
                            percent: Math.round(citation.confidence * 100),
                          })}
                        </Text>
                      </span>
                    ))}
                  </button>
                </li>
              );
            })}
          </ul>
        </Popover.Content>
      </Popover.Root>
      <Dialog.Root
        open={spot != null}
        onOpenChange={(open) => {
          if (!open) setOpenSpotKey(null);
        }}
      >
        {spot ? <PackCitationDialogContent citations={spot.citations} /> : null}
      </Dialog.Root>
    </>
  );
}
